// src/pages/Dashboard/index.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header";
import { primaryGreen } from "../../utils/colors";
import NotFoundPage from "./404";
import GetAdmin from "../../utils/getAdmin";
import Loading from "../loading";
import Navigation from "../Navigation";
import ChartCard from "../charts/chartCard";
import { getChartsInfo } from "../../api/charts";

import {
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  UsersIcon,
  TicketIcon,
  UserPlusIcon,
  UserGroupIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";

// ==================================================================
// تب‌های بازه‌ی زمانی
// ==================================================================
const RANGE_TABS = [
  { key: "weekly", label: "۷ روز اخیر" },
  { key: "fourWeeks", label: "۴ هفته اخیر" },
  { key: "monthly", label: "۶ ماه اخیر" },
];

// ==================================================================
// کارت آماری کوچیک
// ==================================================================
const MiniStatCard = ({ label, value, color, icon: Icon }) => (
  <div
    className="flex-1 min-w-[140px] bg-white rounded-lg border border-gray-100 shadow-sm p-4"
    style={{ borderRight: `4px solid ${color}` }}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color }}>
          {value}
        </p>
      </div>
      {Icon && <Icon className="h-8 w-8 opacity-60" style={{ color }} />}
    </div>
  </div>
);

// ==================================================================
// کارت آماری اصلی
// ==================================================================
const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
  <div
    onClick={onClick}
    className={`text-white p-4 rounded-lg shadow-md transition-all duration-200 ${
      onClick ? "cursor-pointer hover:brightness-90 hover:scale-105" : ""
    }`}
    style={{ backgroundColor: color }}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-80">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
      {Icon && <Icon className="h-10 w-10 opacity-80" />}
    </div>
  </div>
);

// ==================================================================
// 🆕 گرفتن تعداد کل معلم‌های منحصربه‌فرد
// ==================================================================
function getTotalTeachers(rangeData) {
  if (!rangeData) return 0;

  // حالت ۱: بک‌اند totalTeachers عددی داده ✅
  if (typeof rangeData.totalTeachers === "number") {
    return rangeData.totalTeachers;
  }

  // حالت ۲: fallback — جمع همه معلم‌های یونیک در همه بازه‌ها
  if (Array.isArray(rangeData.uniqueTeachers)) {
    return rangeData.uniqueTeachers.reduce(
      (sum, item) => sum + (item.count ?? 0),
      0,
    );
  }

  return 0;
}

// ==================================================================
// جمع کل کدهای تخفیف در یک بازه
// ==================================================================
function getTotalDiscounts(rangeData) {
  if (!rangeData || !Array.isArray(rangeData.discounts)) return 0;
  return rangeData.discounts.reduce((sum, d) => sum + (d.count ?? 0), 0);
}

// ==================================================================
// کامپوننت اصلی
// ==================================================================
const Dashboard = () => {
  const [adminLoading, setAdminLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [chartsData, setChartsData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("weekly");
  const navigate = useNavigate();

  // ============ ادمین ============
  useEffect(() => {
    let isMounted = true;

    const fetchAdmin = async () => {
      try {
        const admin = await GetAdmin();
        if (!isMounted) return;

        if (!admin || admin === "empty") {
          setErrorMsg("لطفاً دوباره وارد شوید");
          return;
        }
        setAdminData(admin);
      } catch (err) {
        console.error("Error fetching admin:", err);
        if (isMounted) setErrorMsg("خطا در دریافت اطلاعات ادمین");
      } finally {
        if (isMounted) setAdminLoading(false);
      }
    };

    fetchAdmin();
    return () => {
      isMounted = false;
    };
  }, []);

  // ============ نمودارها ============
  useEffect(() => {
    if (!adminData || adminData.role !== "manager") return;

    let isMounted = true;
    setChartsLoading(true);

    const fetchCharts = async () => {
      try {
        const res = await getChartsInfo();
        console.log(res);
        if (!isMounted) return;
        if (res?.data) setChartsData(res.data);
      } catch (err) {
        console.error("Error fetching charts:", err);
      } finally {
        if (isMounted) setChartsLoading(false);
      }
    };

    fetchCharts();
    return () => {
      isMounted = false;
    };
  }, [adminData]);

  // ============ حالت‌های نمایش ============
  if (adminLoading) return <Loading />;

  if (errorMsg) {
    return (
      <div className="p-6 text-center text-red-600 font-medium">{errorMsg}</div>
    );
  }

  if (!adminData) {
    return <div className="p-6 text-center">در حال دریافت اطلاعات...</div>;
  }

  if (adminData.role !== "manager") return <NotFoundPage />;

  // ============ داده‌های آماده ============
  const today = chartsData?.today ?? {
    worksheets: 0,
    practices: 0,
    users: 0,
    discounts: 0,
    registrations: 0,
  };

  const totalUsers = chartsData?.totalUsers ?? 0;

  const teachersWeekly = getTotalTeachers(chartsData?.weekly);
  const teachersFourWeeks = getTotalTeachers(chartsData?.fourWeeks);
  const teachersMonthly = getTotalTeachers(chartsData?.monthly);

  console.log(chartsData?.monthly);

  const discountsWeekly = getTotalDiscounts(chartsData?.weekly);
  const discountsFourWeeks = getTotalDiscounts(chartsData?.fourWeeks);
  const discountsMonthly = getTotalDiscounts(chartsData?.monthly);

  const activeData = chartsData?.[activeTab] ?? {};

  // ============ رندر ============
  return (
    <div className="w-full flex justify-center flex-col items-center">
      <Header adminName={adminData.name} />

      <div className="flex flex-col md:flex-row gap-6 justify-between w-full">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>

        <div className="w-full md:w-3/4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-800">داشبورد</h2>

            {/* ======================================================
                کارت‌های آماری امروز
            ====================================================== */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard
                title="کل کاربران"
                value={chartsLoading ? "..." : totalUsers}
                icon={UserGroupIcon}
                color="#0EA5E9"
              />
              <StatCard
                title="ثبت‌نام‌های امروز"
                value={chartsLoading ? "..." : today.registrations}
                icon={UserPlusIcon}
                color="#10B981"
              />
              <StatCard
                title="کاربران فعال امروز"
                value={chartsLoading ? "..." : today.users}
                icon={UsersIcon}
                color="#3B82F6"
              />
              <StatCard
                title="کاربرگ‌های امروز"
                value={chartsLoading ? "..." : today.worksheets}
                icon={ClipboardDocumentListIcon}
                color={primaryGreen}
              />
              <StatCard
                title="تمرین‌های امروز"
                value={chartsLoading ? "..." : today.practices}
                icon={PencilSquareIcon}
                color="#F59E0B"
              />
              <StatCard
                title="کدهای تخفیف امروز"
                value={chartsLoading ? "..." : today.discounts}
                icon={TicketIcon}
                color="#8B5CF6"
              />
            </div>

            {/* ======================================================
                معلم‌های فعال — خلاصه سه بازه
            ====================================================== */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <AcademicCapIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-base font-bold text-gray-700">
                  معلم‌های فعال (منحصربه‌فرد)
                </h3>
              </div>

              <div className="flex flex-wrap gap-4">
                <MiniStatCard
                  label="۷ روز اخیر"
                  value={chartsLoading ? "..." : teachersWeekly}
                  color="#075B4A"
                  icon={AcademicCapIcon}
                />
                <MiniStatCard
                  label="۴ هفته اخیر"
                  value={chartsLoading ? "..." : teachersFourWeeks}
                  color="#F59E0B"
                  icon={AcademicCapIcon}
                />
                <MiniStatCard
                  label="۶ ماه اخیر"
                  value={chartsLoading ? "..." : teachersMonthly}
                  color="#8B5CF6"
                  icon={AcademicCapIcon}
                />
              </div>
            </div>

            {/* ======================================================
                کدهای تخفیف — خلاصه سه بازه
            ====================================================== */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <TicketIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-base font-bold text-gray-700">
                  کدهای تخفیف ساخته‌شده
                </h3>
              </div>

              <div className="flex flex-wrap gap-4">
                <MiniStatCard
                  label="۷ روز اخیر"
                  value={chartsLoading ? "..." : discountsWeekly}
                  color="#EC4899"
                  icon={TicketIcon}
                />
                <MiniStatCard
                  label="۴ هفته اخیر"
                  value={chartsLoading ? "..." : discountsFourWeeks}
                  color="#F43F5E"
                  icon={TicketIcon}
                />
                <MiniStatCard
                  label="۶ ماه اخیر"
                  value={chartsLoading ? "..." : discountsMonthly}
                  color="#A855F7"
                  icon={TicketIcon}
                />
              </div>
            </div>

            {/* ======================================================
                تب‌ها
            ====================================================== */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="text-base font-bold text-gray-700">
                آمار فعالیت‌ها
              </h3>

              <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                {RANGE_TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`px-3 py-1.5 rounded-md text-sm transition ${
                      activeTab === t.key
                        ? "bg-white text-[#075B4A] font-medium shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {chartsLoading ? (
              <div className="text-center py-16 text-gray-500">
                در حال بارگذاری نمودارها...
              </div>
            ) : (
              <>
                {/* ========== چهار نمودار کوچک ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <ChartCard
                    title="کاربرگ‌های امتحانی"
                    type="line"
                    series={[
                      {
                        key: "worksheets",
                        label: "کاربرگ‌ها",
                        data: activeData.worksheets ?? [],
                      },
                    ]}
                  />
                  <ChartCard
                    title="تمرین‌ها"
                    type="line"
                    series={[
                      {
                        key: "practices",
                        label: "تمرین‌ها",
                        data: activeData.practices ?? [],
                      },
                    ]}
                  />
                  <ChartCard
                    title="ثبت‌نام‌ها"
                    type="line"
                    series={[
                      {
                        key: "registrations",
                        label: "ثبت‌نام",
                        data: activeData.registrations ?? [],
                      },
                    ]}
                  />
                  <ChartCard
                    title="کدهای تخفیف"
                    type="line"
                    series={[
                      {
                        key: "discounts",
                        label: "کد تخفیف",
                        data: activeData.discounts ?? [],
                      },
                    ]}
                  />
                </div>

                {/* ========== نمودار بزرگ معلم‌ها ========== */}
                <div className="mb-6">
                  <ChartCard
                    title={`معلم‌های فعال (منحصربه‌فرد) — ${
                      RANGE_TABS.find((t) => t.key === activeTab)?.label
                    }`}
                    type="line"
                    height={420}
                    big
                    series={[
                      {
                        key: "teachers",
                        label: "معلم‌ها",
                        data: activeData.uniqueTeachers ?? [],
                      },
                    ]}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
